import React, { useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  HelpCircle,
  BookOpen,
  Video,
  MessageCircle,
  ExternalLink,
  ChevronRight,
  Lightbulb,
  Zap,
  ArrowRight
} from 'lucide-react';

// Help content for different pages/features
export const helpContent: Record<string, {
  title: string;
  description: string;
  tips?: string[];
  videoUrl?: string;
  docUrl?: string;
}> = {
  dashboard: {
    title: 'Content Manager Dashboard',
    description: 'Your command center for managing product content. View key metrics, track progress, and access quick actions.',
    tips: [
      'Use Quick Actions to navigate to common tasks faster',
      'Check Market Intelligence for content opportunities',
      'Review the onboarding checklist to ensure you complete all setup steps'
    ]
  },
  'data-sources': {
    title: 'Data Sources',
    description: 'Upload and manage your product feeds. Supports CSV, XML, and JSON formats.',
    tips: [
      'Schedule automatic feed imports for regular updates',
      'Map your feed columns to PageMind attributes for better enrichment',
      'Check the import history to troubleshoot any issues'
    ]
  },
  products: {
    title: 'Products (Offering)',
    description: 'View and manage your product catalog. Run AI enrichment to enhance product data.',
    tips: [
      'Use filters to find products that need enrichment',
      'Run batch enrichment for multiple products at once',
      'Review AI-suggested attributes before approving'
    ]
  },
  'enrichment-review': {
    title: 'Enrichment Review',
    description: 'Review and approve AI-generated product attributes and descriptions.',
    tips: [
      'Focus on high-value products first',
      'Use bulk approve for similar products',
      'Flag any incorrect enrichments for AI improvement'
    ]
  },
  aeo: {
    title: 'AEO Content',
    description: 'Create Answer Engine Optimization pages that answer customer questions and boost SEO.',
    tips: [
      'Focus on categories with high search volume first',
      'Include product recommendations in your AEO pages',
      'Regularly update AEO content to keep it fresh'
    ]
  },
  'qa-system': {
    title: 'Q&A System',
    description: 'Manage questions and answers for your products and categories.',
    tips: [
      'Review auto-generated Q&A for accuracy',
      'Add custom questions based on customer feedback',
      'Link related questions to improve navigation'
    ]
  },
  'ai-articles': {
    title: 'AI Articles',
    description: 'Generate blog posts and content using AI based on your products and market data.',
    tips: [
      'Use Market Intelligence insights to choose topics',
      'Review and edit AI-generated content before publishing',
      'Include internal links to products and categories'
    ]
  },
  'feed-export': {
    title: 'Feed Export',
    description: 'Export your enriched product data in various formats for use in other systems.',
    tips: [
      'Choose the right format for your destination (Google Shopping, Meta, etc.)',
      'Schedule automatic exports for regular updates',
      'Test exports with a small batch first'
    ]
  },
  'market-intelligence': {
    title: 'Market Intelligence',
    description: 'View search volume data and identify content opportunities based on market demand.',
    tips: [
      'Prioritize categories with high search volume and low competition',
      'Use customer intents to guide content creation',
      'Check opportunities regularly as market trends change'
    ]
  }
};

interface HelpTooltipProps {
  helpKey: string;
  children: React.ReactNode;
}

/**
 * Wrap any element with a help tooltip
 */
export function HelpTooltip({ helpKey, children }: HelpTooltipProps) {
  const content = helpContent[helpKey];

  if (!content) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium">{content.title}</p>
            <p className="text-sm text-muted-foreground">{content.description}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface HelpIconProps {
  helpKey: string;
  className?: string;
}

/**
 * Small help icon that shows a tooltip on hover
 */
export function HelpIcon({ helpKey, className = '' }: HelpIconProps) {
  const content = helpContent[helpKey];

  if (!content) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <HelpCircle className={`h-4 w-4 text-muted-foreground hover:text-foreground cursor-help ${className}`} />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm">
          <div className="space-y-2">
            <p className="font-medium">{content.title}</p>
            <p className="text-sm text-muted-foreground">{content.description}</p>
            {content.tips && content.tips.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs font-medium flex items-center gap-1 mb-1">
                  <Lightbulb className="h-3 w-3" /> Tips
                </p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {content.tips.slice(0, 2).map((tip, i) => (
                    <li key={i}>• {tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface HelpPanelProps {
  currentPage?: string;
}

/**
 * Full help panel that slides in from the right
 */
export function HelpPanel({ currentPage }: HelpPanelProps) {
  const [open, setOpen] = useState(false);
  const content = currentPage ? helpContent[currentPage] : null;

  const quickLinks = [
    { label: 'Getting Started Guide', icon: BookOpen, href: '#' },
    { label: 'Video Tutorials', icon: Video, href: '#' },
    { label: 'Contact Support', icon: MessageCircle, href: 'mailto:support@pagemind.ai' }
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="fixed bottom-6 right-6 shadow-lg z-50"
        >
          <HelpCircle className="h-4 w-4 mr-2" />
          Help
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-500" />
            Help & Resources
          </SheetTitle>
          <SheetDescription>
            Get help with PageMind features and workflows
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Current Page Help */}
          {content && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h3 className="font-semibold flex items-center gap-2 text-blue-900">
                <Zap className="h-4 w-4" />
                {content.title}
              </h3>
              <p className="text-sm text-blue-700 mt-1">{content.description}</p>

              {content.tips && content.tips.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">
                    Pro Tips
                  </p>
                  {content.tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-blue-700">
                      <Lightbulb className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quick Links */}
          <div>
            <h4 className="font-medium mb-3">Quick Links</h4>
            <div className="space-y-2">
              {quickLinks.map((link, i) => (
                <a
                  key={i}
                  href={link.href}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <link.icon className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm font-medium">{link.label}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </a>
              ))}
            </div>
          </div>

          {/* Common Workflows */}
          <div>
            <h4 className="font-medium mb-3">Common Workflows</h4>
            <div className="space-y-3">
              <WorkflowCard
                title="Enrich Products with AI"
                steps={['Upload feed', 'Select products', 'Run enrichment', 'Review results']}
              />
              <WorkflowCard
                title="Create AEO Content"
                steps={['Choose category', 'Generate content', 'Edit & customize', 'Publish']}
              />
              <WorkflowCard
                title="Export Enriched Feed"
                steps={['Select products', 'Choose format', 'Configure mapping', 'Download']}
              />
            </div>
          </div>

          {/* Feature Overview */}
          <div>
            <h4 className="font-medium mb-3">All Features</h4>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(helpContent).map(([key, value]) => (
                <Badge
                  key={key}
                  variant="outline"
                  className="justify-start py-2 px-3 cursor-pointer hover:bg-muted"
                >
                  {value.title}
                </Badge>
              ))}
            </div>
          </div>

          {/* Contact Support */}
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-1">Need More Help?</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <Button variant="outline" size="sm" asChild>
              <a href="mailto:support@pagemind.ai">
                <MessageCircle className="h-4 w-4 mr-2" />
                Contact Support
              </a>
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface WorkflowCardProps {
  title: string;
  steps: string[];
}

function WorkflowCard({ title, steps }: WorkflowCardProps) {
  return (
    <div className="p-3 rounded-lg border bg-card">
      <h5 className="text-sm font-medium mb-2">{title}</h5>
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <span className="bg-muted px-2 py-0.5 rounded">{step}</span>
            {i < steps.length - 1 && <ArrowRight className="h-3 w-3 flex-shrink-0" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default HelpPanel;
